import { useEffect, useMemo, useState } from 'react';
import { useChat } from '../../chat/ChatProvider';

/**
 * @ 選人面板：來源為當前會話 JOINED 成員（不含自己）。
 */
export default function MentionPicker({ conversationId, query, onPick, onClose }) {
  const { state, myMemberId, refreshSummaries } = useChat();
  const [ready, setReady] = useState(false);

  const conv = useMemo(
    () =>
      (state.conversations || []).find(
        (c) => String(c.conversationId || c.id) === String(conversationId)
      ),
    [state.conversations, conversationId]
  );

  const candidateIds = useMemo(() => {
    const ids = new Set();
    (conv?.participantMemberIds || []).forEach((id) => {
      if (id != null && String(id) !== String(myMemberId)) ids.add(String(id));
    });
    if (conv?.peerMemberId != null && String(conv.peerMemberId) !== String(myMemberId)) {
      ids.add(String(conv.peerMemberId));
    }
    return [...ids];
  }, [conv, myMemberId]);

  useEffect(() => {
    if (!candidateIds.length) {
      setReady(true);
      return;
    }
    let cancelled = false;
    refreshSummaries(candidateIds.map(Number)).finally(() => {
      if (!cancelled) setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [candidateIds.join(','), refreshSummaries]);

  const q = (query || '').trim().toLowerCase();
  const rows = useMemo(() => {
    const summaries = state.participantSummaries || {};
    return candidateIds
      .map((id) => {
        const s = summaries[id] || summaries[Number(id)];
        const username = s?.username || '';
        const displayName = s?.displayName || '';
        const label = username || displayName || `用戶 ${id}`;
        return { memberId: id, username, displayName, label };
      })
      .filter((row) => {
        if (!q) return true;
        return (
          row.username.toLowerCase().includes(q) ||
          row.displayName.toLowerCase().includes(q) ||
          String(row.memberId).includes(q)
        );
      })
      .slice(0, 8);
  }, [candidateIds, state.participantSummaries, q]);

  if (!ready) {
    return <div className="chat-mention-picker chat-mention-picker--loading">載入成員…</div>;
  }
  if (!rows.length) {
    return (
      <div className="chat-mention-picker">
        <div className="chat-mention-picker__empty">無可提及的成員</div>
        <button type="button" className="chat-link-btn" onClick={onClose}>
          關閉
        </button>
      </div>
    );
  }

  return (
    <div className="chat-mention-picker" role="listbox" aria-label="提及成員">
      {rows.map((row) => (
        <button
          key={row.memberId}
          type="button"
          className="chat-mention-picker__item"
          onClick={() => onPick(row)}
        >
          <strong>{row.label}</strong>
          {row.username && row.displayName && row.username !== row.displayName ? (
            <small>{row.displayName}</small>
          ) : null}
        </button>
      ))}
    </div>
  );
}
